import { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { ChartFilterBar, HorizontalStackedBarChartCard } from "../../../components/Charts";
import type { FilterDefinition, FilterValues } from "../../../types/chartFilters";
import type { FilterOnChange } from "../../../components/Charts/ChartFilterBar";
import { CHART_DIMENSION_PALETTE } from "../../../constants/chartColors";
import {
  getP4DesajustColor,
  P4_DESAJUST_LEVEL_OPTIONS,
  P4_DESAJUST_PERCENTILES,
  useP4Data,
  type P4FilterValues,
} from "./useP4ChartData";
import CompareButton from "../common/CompareButton";
import P4DetailMetricsTableCard from "./P4DetailMetricsTableCard";

const DEFAULT_FILTERS: P4FilterValues = {
  titulacions: [],
  desajustLevels: [],
};

const P4ChartSection = () => {
  const [filters, setFilters] = useState<P4FilterValues>(DEFAULT_FILTERS);
  const { chartData, bars, availableTitulacions, defaultTitulacions, loading, error } =
    useP4Data(filters);

  const [defaultsApplied, setDefaultsApplied] = useState(false);

  useEffect(() => {
    if (!defaultsApplied && defaultTitulacions.length > 0) {
      setFilters((prev) => ({
        ...prev,
        titulacions: defaultTitulacions,
      }));
      setDefaultsApplied(true);
    }
  }, [defaultTitulacions, defaultsApplied]);

  const titulacioOptions = useMemo(
    () => availableTitulacions.map((t) => ({ value: t, label: t })),
    [availableTitulacions]
  );

  const p4Filters: FilterDefinition[] = useMemo(
    () => [
      {
        type:        "multiSelect",
        key:         "titulacions",
        label:       "Titulacions a comparar",
        options:     titulacioOptions,
        placeholder: "Titulacions a comparar",
      },
    ],
    [titulacioOptions]
  );

  const handleFilterChange: FilterOnChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value } as P4FilterValues));
  };

  const xDomainMax = useMemo(() => {
    const maxVisibleValue = Math.max(
      P4_DESAJUST_PERCENTILES.p66,
      ...chartData.map((point) => point.index_desajust)
    );

    return Number((maxVisibleValue * 1.12).toFixed(3));
  }, [chartData]);
  const noTitulacionsSelected = filters.titulacions.length === 0;
  const selectTitulacionsWarning = "Cal seleccionar almenys una titulació.";

  return (
    <>
      <ChartFilterBar
        filters={p4Filters}
        values={filters as unknown as FilterValues}
        onChange={handleFilterChange}
        disabled={loading}
      />
      <HorizontalStackedBarChartCard
        title="Comparativa de l'índex de desajust laboral per titulació"
        data={chartData}
        yKey="nom_titulacio"
        bars={bars}
        loading={loading}
        error={error}
        xDomain={[0, xDomainMax]}
        tooltipSuffix=""
        stacked={false}
        labelMinThreshold={0}
        emptyMessage={noTitulacionsSelected ? selectTitulacionsWarning : "Sense dades disponibles"}
        emptyIsWarning={noTitulacionsSelected}
        referenceLines={[
          {
            value: P4_DESAJUST_PERCENTILES.p33,
            label: `P33 ${P4_DESAJUST_PERCENTILES.p33}`,
            stroke: CHART_DIMENSION_PALETTE[5],
          },
          {
            value: P4_DESAJUST_PERCENTILES.p66,
            label: `P66 ${P4_DESAJUST_PERCENTILES.p66}`,
            stroke: CHART_DIMENSION_PALETTE[6],
          },
        ]}
        getBarColor={(value) => getP4DesajustColor(value)}
        headerExtra={
          <Select
            mode="multiple"
            showSearch={false}
            value={filters.desajustLevels}
            options={P4_DESAJUST_LEVEL_OPTIONS}
            placeholder="Tipus de desajust"
            onChange={(v) => setFilters((prev) => ({ ...prev, desajustLevels: v }))}
            style={{ minWidth: 220 }}
            maxTagCount="responsive"
            allowClear
            disabled={loading}
          />
        }
      />
      <P4DetailMetricsTableCard
        title="Detall d'indicadors per titulació"
        data={chartData}
        emptyMessage={noTitulacionsSelected ? selectTitulacionsWarning : "Sense dades disponibles"}
        emptyIsWarning={noTitulacionsSelected}
      />
      <CompareButton />
    </>
  );
};

export default P4ChartSection;
